import Portal from '@srcDS/components/atoms/portal';
import PrimaryLoader from '@srcDS/components/atoms/primaryLoader/PrimaryLoader';
import React, { CSSProperties, useRef } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import MenuLink from '@srcDS/components/molecules/headerMenu/menuLink/MenuLink';
import { NavigationBanner } from '@srcDS/components/organisms/header/navigation/NavigationBanner/NavigationBanner';
import { useOutsideClick } from '@srcDS/hooks/useOutsideClick';
import { useMenuContext } from '@srcDS/provider/menu/MenuContext';
import { getHexColor } from '@srcDS/utils/helpers';
import { useMedia } from 'react-use';
import styles from '../DesktopMenu.module.scss';
import {
  getContentfulNavigationTiles,
  mapContentfulToNavigationTile,
} from '@srcDS/components/organisms/header/navigation/NavigationBanner/helpers';
import { useLayoutContext } from '@srcDS/provider/layout/LayoutContext';
import { SecondaryMenuLevelProps } from '@srcDS/components/molecules/headerMenu/desktopMenu/secondaryMenuLevel/SecondaryMenuLevelProps.types';

export const SecondaryMenuLevel = ({
  geoBannerVisible,
  geoBannerHeight,
  isStickyHeader,
  isInvertedClass,
  contentfulNavigationTiles,
  flattened = false,
}: SecondaryMenuLevelProps) => {
  const {
    activeLevel1,
    setActiveLevel1,
    selectedLevel2,
    setSelectedLevel2,
    isTouch,
    setShownMenu,
    getMenuItemData,
    setCurrentLevelName,
    categories,
    hasCategories,
  } = useMenuContext();
  const isDesktop = useMedia('(min-width: 1024px)', true);
  const router = useRouter();
  const { setShowSearchDropdown, isLoadedPage, setIsLoadedPage } = useLayoutContext();

  const linkRef = useRef<HTMLDivElement | null>(null);

  const isVisible = (node: any) => {
    const meta = getMenuItemData(node);
    return meta?.isVisibleInCurrentLocale && meta?.isVisibleInMenu;
  };

  const linkColorOf = (node: any) => {
    const { navItemColor } = getMenuItemData(node) || {};
    return getHexColor(navItemColor) ?? '';
  };

  const navTilesOf = (nodeUrl?: string, nodeTiles?: any[]) => {
    const contentfulTiles = getContentfulNavigationTiles(contentfulNavigationTiles, nodeUrl) ?? [];
    const mapped = contentfulTiles.map(mapContentfulToNavigationTile);
    return mapped.length ? [...(nodeTiles || []), ...mapped] : nodeTiles || [];
  };

  const renderChildrenList = (children?: any[], linkColor?: string) => {
    if (!children?.length) return null;
    return (
      <ul className={styles.nestedItemsWrap}>
        {children.map((child) => {
          if (!isVisible(child)) return null;
          return (
            <li key={child.pageFolderId}>
              <MenuLink category={child} linkColor={linkColorOf(child) || linkColor} />
            </li>
          );
        })}
      </ul>
    );
  };

  useOutsideClick({
    ref: linkRef,
    handler: () => {
      setActiveLevel1(null);
      setSelectedLevel2(null);
      setShownMenu(false);
      setCurrentLevelName(null);
    },
    activate: isDesktop && isTouch,
  });

  const handleMouseOver = (category) => {
    if (!isTouch && selectedLevel2 !== category.name) {
      setShowSearchDropdown(false);
      setSelectedLevel2(category.name);
      setCurrentLevelName(category.name);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouch) {
      setActiveLevel1(null);
      setSelectedLevel2(null);
      setCurrentLevelName(null);
    }
  };

  const handleClick = (event, category) => {
    if (!isTouch && category._url) {
      event.stopPropagation();
      setShownMenu(false);
      setIsLoadedPage(true);
      router.push(category._url);
      return;
    }

    if (isTouch && category.children) {
      setShowSearchDropdown(false);
      setSelectedLevel2(category.name);
      setCurrentLevelName(category.name);
    }
  };

  const handleReferenceClick = (e, isTop, hasChildren) => {
    if (isTouch && isTop && hasChildren) {
      e.preventDefault();
    }
  };

  const containerStyle: CSSProperties = {
    '--geoBannerHeight': geoBannerVisible && geoBannerHeight ? `${geoBannerHeight}px` : undefined,
  };

  if (!activeLevel1 || !hasCategories) return null;

  if (flattened && isDesktop) {
    const activeUrl = activeLevel1?.tree?._url;
    const baseTiles = activeLevel1?.tree?.configuration?.navigationTiles;
    const navigationTiles = navTilesOf(activeUrl, baseTiles);
    const hasNavigationTiles = navigationTiles?.length > 0;

    const allCategoryWithoutChildren = categories.every((c) => !c.children?.length);

    return (
      <div
        className={clsx(
          styles.menuSecondLevel,
          styles.flattened,
          { [styles.hasNavTiles]: hasNavigationTiles },
          styles.active,
        )}
        ref={linkRef}
        onMouseLeave={handleMouseLeave}
      >
        <div data-testid="category" className={styles.mainItem}>
          <div
            className={clsx(styles.column, styles.leftColumn, { [styles.withoutChildren]: allCategoryWithoutChildren })}
          >
            {categories.map((category) => {
              if (!isVisible(category)) return null;
              const linkColor = linkColorOf(category);
              const children = category.children;

              return (
                <div className={styles.columnContainer} key={category.name}>
                  {children?.length ? (
                    <div className={styles.linkTitled}>
                      <MenuLink category={category} linkColor={linkColor} />
                    </div>
                  ) : (
                    <div className={styles.linkText}>
                      <MenuLink category={category} linkColor={linkColor} />
                    </div>
                  )}
                  {renderChildrenList(children, linkColor)}
                </div>
              );
            })}
          </div>
          <div className={clsx(styles.column, styles.rightColumn)}>
            {hasNavigationTiles &&
              activeLevel1.name === 'Women' &&
              navigationTiles
                .slice(0, 2)
                .map((tile, index) => <NavigationBanner key={`navigation-banner-${index}`} tile={tile} />)}
          </div>
        </div>
      </div>
    );
  }

  if (isLoadedPage) {
    return (
      <Portal>
        <PrimaryLoader mode="global" />
      </Portal>
    )
  }

  return (
    <div
      className={clsx(styles.menuSecondLevel, { [styles.active]: activeLevel1 })}
      ref={linkRef}
      onMouseLeave={handleMouseLeave}
    >
      {categories.map((category) => {
        if (!isVisible(category)) return null;

        const { isTop, children, hasChildren } = getMenuItemData(category);
        const linkColor = linkColorOf(category);
        const catTiles = navTilesOf(category?._url, category?.configuration?.navigationTiles);
        const hasNavigationTiles = catTiles?.length > 0;
        const isActiveMenuItem = selectedLevel2 === category.name;

        return (
          <div data-testid="category" className={styles.mainItem} key={category.name}>
            <div
              data-testid="category-inner"
              className={clsx(styles.categoryName, !hasChildren && styles.noChildren, isInvertedClass, {
                [styles.activeMenuItem]: isActiveMenuItem,
              })}
              onMouseOver={() => handleMouseOver(category)}
              onClick={(e) => handleClick(e, category)}
            >
              <MenuLink
                category={category}
                linkColor={linkColor}
                handleReferenceClick={handleReferenceClick}
                isTop={isTop}
                hasChildren={hasChildren}
              />
            </div>
            <div
              className={clsx(
                styles.megamenu,
                { [styles.megamenuShown]: isActiveMenuItem && hasChildren },
                { [styles.isStickyHeader]: isStickyHeader },
                { [styles.geoBannerVisible]: geoBannerVisible },
              )}
              style={containerStyle}
            >
              {hasChildren && (
                <>
                  <div className={clsx(styles.column, styles.leftColumn)}>
                    {renderChildrenList(children, linkColor)}
                  </div>
                  <div className={clsx(styles.column, styles.rightColumn)}>
                    {hasNavigationTiles &&
                      catTiles
                        .slice(0, 2)
                        .map((tile, index) => <NavigationBanner key={`navigation-banner-${index}`} tile={tile} />)}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SecondaryMenuLevel;
